
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

export function useAuthState() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authTimeout, setAuthTimeout] = useState(false);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Effect to manage authentication state
  useEffect(() => {
    let isMounted = true;
    let authTimeoutId: number | null = null;
    let failureTimeoutId: number | null = null;
    
    // Setup timeout for loading state feedback - shorter timeout for better UX
    authTimeoutId = window.setTimeout(() => {
      if (isMounted) setAuthTimeout(true);
    }, 1500);

    const handleSession = async () => {
      try {
        // Get the session from Supabase with explicit persistence settings
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error fetching auth session:", error.message);
          throw error;
        }
        
        if (isMounted) {
          setSession(data.session);
          setUser(data.session?.user ?? null);
          setLoading(false);
          
          // Clear the timeout since we successfully loaded
          if (authTimeoutId) {
            window.clearTimeout(authTimeoutId);
            authTimeoutId = null;
          }
          
          if (failureTimeoutId) {
            window.clearTimeout(failureTimeoutId);
            failureTimeoutId = null;
          }
        }
      } catch (error) {
        console.error("Session fetch error:", error);
        if (isMounted) {
          if (retryCount < 2) {
            // Silent retry
          } else {
            setLoading(false);
            setLoadingFailed(true);
            // Clear sensitive data on authentication failure
            setUser(null);
            setSession(null);
          }
        }
      }
    };

    // Set up a shorter timeout for failure detection or auto-retry
    failureTimeoutId = window.setTimeout(() => {
      if (isMounted && loading) {
        // If we haven't retried too many times, try again
        if (retryCount < 2) {
          setRetryCount(prev => prev + 1);
          // Force a new session check
          handleSession();
        } else {
          // After max retries, give up and show error
          setLoadingFailed(true);
          setLoading(false);
          // Clear sensitive data on authentication failure
          setUser(null);
          setSession(null);
        }
      }
    }, 3000);

    // Initial session check
    handleSession();

    // Set up listener for auth state changes with more secure error handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (isMounted) {
        console.log(`Auth state changed: ${event}`);
        
        if (event === 'SIGNED_OUT') {
          // Explicitly clear user data on signout events
          setUser(null);
          setSession(null);
          // Remove any persisted tokens from localStorage for extra security
          localStorage.removeItem('supabase.auth.token');
        } else if (newSession) {
          setSession(newSession);
          setUser(newSession.user);
        }
        
        setLoading(false); // Ensure loading completes when auth state changes
      }
    });

    return () => {
      isMounted = false;
      // Clear all timeouts to prevent memory leaks and race conditions
      if (authTimeoutId) window.clearTimeout(authTimeoutId);
      if (failureTimeoutId) window.clearTimeout(failureTimeoutId);
      // Clean up auth subscription to prevent memory leaks
      subscription?.unsubscribe();
    };
  }, [retryCount]); // Add retryCount as dependency so the effect reruns when we need to retry

  const retryAuth = () => {
    setLoadingFailed(false);
    setLoading(true);
    setRetryCount(0);
    
    // Clear any potentially corrupt authentication data
    localStorage.removeItem('supabase.auth.token');
    
    // Force browser to reload auth state from scratch
    window.location.reload();
  };

  return { 
    user, 
    session, 
    loading, 
    authTimeout, 
    loadingFailed, 
    retryAuth 
  };
}
